import { Observable, Subscription } from 'rxjs';
import {
  defaultIfEmpty,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { UseTrackingProps } from '../Tracking';
import { Media, MEDIA_TIMEUPDATE, SCTE_TIMEDEVENTS, VIEW_METADATAUPDATED } from '../../types';
import { buildSeekableInformationValue, SeekableMediaInput } from '../../util/seekable';
import UnifiedPlayer from '../../core/UnifiedPlayer';
import * as Epg from '../../modules/dataProviders/Epg';

export function metadataPluginFactory(
  epgOptions: UseTrackingProps['epgOptions'],
  includeStartOverInformation: boolean = false
) {
  const subscriptionToCleanOnDestroy: Subscription[] = [];
  function unsubscribeAllSubscriptions() {
    subscriptionToCleanOnDestroy.forEach(s => s.unsubscribe());
  }

  function mediaToSeekableInput(media: Media): SeekableMediaInput {
    return {
      metadata: {
        isLive: media.metadata.isLive!,
        isStartOver: media.metadata.isStartOver!,
        startTime: media.metadata.startTime!,
        title: media.metadata.title!,
        duration: media.metadata.duration!,
      },
    };
  }

  const plugin = function metadataPlugin(media$: Observable<Media>, player: UnifiedPlayer) {
    const epgPoll$ = Epg.poll$(media$, epgOptions, includeStartOverInformation);

    const timeupdate$ = player.observe(MEDIA_TIMEUPDATE);
    const programEndScteMarker$ = player.observe(SCTE_TIMEDEVENTS).pipe(
      startWith({ endTime: null }),
      map(event => event.endTime)
    );

    const seekableInformation$ = epgPoll$.pipe(
      defaultIfEmpty(null),
      withLatestFrom(media$),
      switchMap(([epg, media]) =>
        timeupdate$.pipe(
          withLatestFrom(programEndScteMarker$),
          map(([time, endMarker]) =>
            buildSeekableInformationValue(
              mediaToSeekableInput(media),
              endMarker,
              epg,
              time.currentTime!
            )
          )
        )
      ),
      distinctUntilChanged((a, b) => a.start === b.start)
    );
    subscriptionToCleanOnDestroy.push(
      // eslint-disable-next-line rxjs/no-ignored-subscription, rxjs/no-ignored-error
      seekableInformation$.subscribe(seekableInformation => {
        player.emit(VIEW_METADATAUPDATED, seekableInformation);
      })
    );
  };
  plugin.destroy = unsubscribeAllSubscriptions;

  return plugin;
}
